/**
 * Time & Action section (garment Increment 4, M5) — section 3 of the Style File.
 *
 * The milestone calendar generated from the garment-type template anchored to
 * ex-factory (`prototype/garment/style-file.html` #sec-ta). A summary strip
 * (PCD · total · done · slipped) over a dated milestone list with per-milestone
 * status (done / current / overdue / future), inline re-plan, and mark-done.
 * (The horizontal spine viz + a cross-style /critical-path board are follow-ons.)
 *
 * @see prototype/garment/style-file.html #sec-ta
 * @see packages/domain/src/garment/tna
 */
import { Calendar, Check, RotateCcw, TriangleAlert, Undo2 } from "lucide-react";
import { Button, toast } from "@flint/ui";
import type { tna as tnaContract } from "@flint/api-contract";
import { trpc } from "../../../lib/trpc.js";
import { formatDate } from "../../../lib/format.js";

type TnaData = tnaContract.TnaViewWire;
type Milestone = TnaData["milestones"][number];

const DOT: Record<Milestone["status"], string> = {
  done: "bg-emerald-500",
  current: "bg-sky-500",
  overdue: "bg-rose-500",
  future: "bg-line-2",
};

interface TnaSectionProps {
  styleId: string;
}

export function TnaSection({ styleId }: TnaSectionProps) {
  const utils = trpc.useUtils();
  const query = trpc.tna.getForStyle.useQuery({ styleId });

  const invalidate = async () => {
    await Promise.all([
      utils.tna.getForStyle.invalidate({ styleId }),
      utils.auditLog.list.invalidate(),
    ]);
  };
  const onErr = (verb: string) => (e: { message: string }) => toast.error(`Couldn't ${verb}`, { description: e.message });

  const generate = trpc.tna.generate.useMutation({
    onSuccess: async (r: { count: number }) => { await invalidate(); toast.success("T&A generated", { description: `${r.count} milestones scheduled.` }); },
    onError: onErr("generate the schedule"),
  });
  const update = trpc.tna.updateMilestone.useMutation({ onSuccess: invalidate, onError: onErr("update the milestone") });

  if (query.isLoading) return <p className="text-sm text-muted">Loading T&amp;A…</p>;
  const data = query.data;
  if (!data) return <p className="text-sm text-muted">No T&amp;A data.</p>;

  if (!data.hasTna) {
    return (
      <div className="rounded-sm border border-dashed border-line bg-bg px-4 py-6 text-center">
        <p className="text-sm font-medium text-ink-2">No schedule yet</p>
        <p className="mx-auto mt-1 max-w-md text-xs leading-relaxed text-muted">
          {data.exFactoryDate
            ? "Generate the Time & Action calendar from the garment-type template, anchored to the ex-factory date."
            : "Set the style's ex-factory date first — the T&A milestones anchor to it."}
        </p>
        {data.exFactoryDate ? (
          <Button className="mt-3" disabled={generate.isPending} onClick={() => generate.mutate({ styleId })}>
            <Calendar size={15} /> Generate schedule
          </Button>
        ) : null}
      </div>
    );
  }

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="flex flex-col gap-3">
      {/* Summary strip */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-sm border border-line bg-bg px-3 py-2 text-xs">
        <Stat label="PCD" value={data.pcdDate ? formatDate(data.pcdDate) : "—"} />
        <Stat label="Ex-factory" value={data.exFactoryDate ? formatDate(data.exFactoryDate) : "—"} />
        <Stat label="Milestones" value={`${data.doneCount}/${data.totalMilestones} done`} />
        {data.overdueCount > 0 ? (
          <span className="inline-flex items-center gap-1 font-semibold text-warn">
            <TriangleAlert size={13} /> {data.overdueCount} overdue · {data.slippageDays}d slipped
          </span>
        ) : (
          <span className="font-medium text-emerald-600">On track</span>
        )}
        <button type="button" disabled={generate.isPending} onClick={() => generate.mutate({ styleId })} title="Regenerate from the template" className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-muted hover:text-ink-2 disabled:opacity-40">
          <RotateCcw size={12} /> Regenerate
        </button>
      </div>

      {/* Milestone list */}
      <ol className="flex flex-col">
        {data.milestones.map((m) => {
          const busy = update.isPending;
          return (
            <li key={m.id} className="flex items-center gap-3 border-b border-line py-2 last:border-b-0">
              <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${DOT[m.status]}`} aria-hidden />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-sm">
                  <span className={`font-medium ${m.status === "done" ? "text-muted line-through" : "text-ink"}`}>{m.label}</span>
                  {m.status === "current" ? <span className="rounded-full bg-sky-50 px-1.5 text-xs font-semibold text-sky-700">Now</span> : null}
                  {m.status === "overdue" ? <span className="rounded-full bg-rose-50 px-1.5 text-xs font-semibold text-rose-700">{m.daysOverdue}d late</span> : null}
                </div>
                {m.ownerRole ? <div className="text-xs capitalize text-muted">{m.ownerRole}</div> : null}
              </div>
              <label className="flex items-center gap-1 text-xs text-muted">
                <span className="hidden sm:inline">Planned</span>
                <input type="date" value={m.plannedDate} disabled={busy} onChange={(e) => { const plannedDate = e.currentTarget.value; if (plannedDate) update.mutate({ milestoneId: m.id, plannedDate }); }} className="h-7 rounded-sm border border-line bg-paper px-1.5 font-mono text-xs text-ink-2 outline-none focus-visible:border-sky-600" />
              </label>
              {m.actualDate ? (
                <button type="button" disabled={busy} onClick={() => update.mutate({ milestoneId: m.id, actualDate: null })} className="inline-flex items-center gap-1 rounded-sm border border-line bg-paper px-1.5 py-1 text-xs font-medium text-muted hover:bg-bg disabled:opacity-40" title={`Done ${m.actualDate}`}>
                  <Undo2 size={12} /> {formatDate(m.actualDate)}
                </button>
              ) : (
                <button type="button" disabled={busy} onClick={() => update.mutate({ milestoneId: m.id, actualDate: today })} className="inline-flex items-center gap-1 rounded-sm border border-emerald-200 bg-paper px-1.5 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-40">
                  <Check size={12} /> Mark done
                </button>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <span className="text-muted">
      <span className="font-semibold uppercase tracking-[0.04em] text-xs">{label}</span> <span className="font-medium text-ink-2">{value}</span>
    </span>
  );
}
